import { DateRange } from '@common/data-models/complex-types/date-range';
import { ViewStateActions, ViewStateApiActions } from '@common/store/navigation/actions/viewstate.actions';
import { ViewStateState } from '@common/store/navigation/models/viewstate.models';
import { createReducer, on } from '@ngrx/store';
import { DateTime } from '@src/suite/common/data-models/complex-types/date-time';
import { PSTFieldNamesEnum } from '@src/suite/common/data-models/fields/pst-field-names';
import { FilterDef } from '@src/suite/common/data-models/navigation/filter-def';
import { PublicationViewStateRecord } from '@src/suite/common/data-models/records/publication-viewstate-record';
import { RecordFactory } from '@src/suite/common/data-models/records/record-factory';
import { SearchViewStateRecord } from '@src/suite/common/data-models/records/search-viewstate-record';
import { SingleViewStateRecord } from '@src/suite/common/data-models/records/single-viewstate-record';
import { TopicViewStateRecord } from '@src/suite/common/data-models/records/topic-viewstate-record';
import { ViewStateRecord } from '@src/suite/common/data-models/records/viewstate-record';
import { PUBLICATION_LIST_TIME_DAYS_MAP } from '@src/suite/common/enums/constants';
import { ViewStateFilterElevations, ViewStateFilterStates } from '@src/suite/common/enums/viewstate-filter-states';
import { ViewStateLayouts } from '@src/suite/common/enums/viewstate-layouts';
import { ViewStateSortOders } from '@src/suite/common/enums/viewstate-sort-orders';
import { NavigationApiActions } from '@src/suite/common/store/navigation/actions/navigation.actions';

export const initialState: ViewStateState = {
    applicationName: null,
    viewState: null,
    slideshowViewState: null,
    planningViewsFilterState: ViewStateFilterStates.Collapsed,
    filterElevation: 'side',
    mainCta: null,
    secondaryCta: null,
    viewOptions: null,
    rightSlot: null,
};

function handleUpdateViewState(state: ViewStateState, vs: ViewStateRecord): ViewStateState {
    if (state.viewState?.guid !== vs.guid) {
        return state;
    }
    const newViewState = RecordFactory.createViewStateForType(vs.viewStateType);
    vs.copyDataTo(newViewState, true);
    if (newViewState instanceof PublicationViewStateRecord) {
        newViewState.maxPublicationRevisionNumber = (vs as PublicationViewStateRecord).maxPublicationRevisionNumber;
        newViewState.publicationRevisionNumber = (vs as PublicationViewStateRecord).publicationRevisionNumber;
    }
    return {
        ...state,
        viewState: newViewState,
    };
}

export const viewStateReducer = createReducer(
    initialState,
    on(ViewStateActions.setCurrentApplication, (state, action): ViewStateState => {
        return {
            ...state,
            applicationName: action.application,
        };
    }),
    on(ViewStateActions.setCurrentViewStateSuccess, (state, action): ViewStateState => {
        if (action.vs) {
            const vs: ViewStateRecord = RecordFactory.createViewStateForType<SingleViewStateRecord>(action.vs.viewStateType);
            action.vs.copyDataTo(vs, true);

            if (vs instanceof SingleViewStateRecord && action.layout) {
                const layout = action.layout.deepCopy();
                layout.Value = vs.viewStateLayout.Value || layout.Default;
                vs.viewStateLayout = layout;
            }

            if (vs instanceof SingleViewStateRecord && action.sortBy) {
                const sortBy = action.sortBy.deepCopy();
                sortBy.Value = vs.viewStateSorting.Value || sortBy.Default;
                vs.viewStateSorting = sortBy;
            }

            if (vs instanceof SingleViewStateRecord && action.groupBy) {
                const groupBy = action.groupBy.deepCopy();
                groupBy.Value = vs.viewStateGrouping.Value || groupBy.Default;
                vs.viewStateGrouping = groupBy;
            }

            if (vs instanceof SingleViewStateRecord || vs instanceof PublicationViewStateRecord) {
                if (vs instanceof TopicViewStateRecord || vs instanceof SearchViewStateRecord || vs instanceof PublicationViewStateRecord) {
                    if (vs instanceof PublicationViewStateRecord) {
                        if (vs.viewStateLayout.Value === ViewStateLayouts.CARD) {
                            vs.viewStateLayout.Default = ViewStateLayouts.CONDENSE;
                            vs.viewStateLayout.Value = ViewStateLayouts.CONDENSE;
                        }
                        // add publication time
                        const publicationTimeFilter = vs.viewStateFilter.allFilters.find(f => f.ID === PSTFieldNamesEnum.PSTPublicationTime);
                        if (publicationTimeFilter?.Value && PUBLICATION_LIST_TIME_DAYS_MAP[publicationTimeFilter.Value] != null) {
                            const dt = new DateTime();
                            const d = new Date();
                            d.setHours(0, 0, 0, 0);
                            d.setDate(d.getDate() + PUBLICATION_LIST_TIME_DAYS_MAP[publicationTimeFilter.Value]);
                            dt.set(d);
                            vs.publicationTime = dt;
                        }
                    }
                    // Set default custom card fields
                    if (!vs.viewStateCustomCardItems || !Array.isArray(vs.viewStateCustomCardItems)) {
                        (<SingleViewStateRecord>vs).isViewStateCustomCardItemsBlank = true;
                    } else {
                        (<SingleViewStateRecord>vs).isViewStateCustomCardItemsBlank = false;
                    }
                }
            }

            if (vs instanceof SingleViewStateRecord || vs instanceof PublicationViewStateRecord) {
                (<SingleViewStateRecord>vs).isViewStateCustomCardItemsBlank = (<SingleViewStateRecord>vs).isViewStateCustomCardItemsBlank;
            }

            return {
                ...state,
                viewState: vs,
            };
        } else {
            return {
                ...state,
                slideshowViewState: null,
                viewState: null,
            };
        }
    }),
    on(ViewStateActions.setCurrentSlideshowViewState, (state, action): ViewStateState => {
        return {
            ...state,
            slideshowViewState: action.vs,
        };
    }),
    on(ViewStateActions.addViewStateFilters, (state, action): ViewStateState => {
        if (state.viewState instanceof SingleViewStateRecord) {
            const vs = <SingleViewStateRecord>RecordFactory.createViewStateForType(state.viewState.viewStateType);
            state.viewState.copyDataTo(vs, true);

            const filter = state.viewState.viewStateFilter.deepCopy();
            const filters: Array<FilterDef> = filter.allFilters.filter(item => {
                return !!action.filters.find(f => f === item.ID);
            });
            if (action.required) {
                filter.requiredFilters = filters;
                filter.onFilterStateChange(true);
            } else {
                filter.optionalFilters = filters;
                filter.onFilterStateChange(false);
            }
            vs.viewStateFilter = filter;

            return {
                ...state,
                viewState: vs,
            };
        }
    }),
    on(ViewStateActions.removeViewStateFilter, (state, action): ViewStateState => {
        if (state.viewState instanceof SingleViewStateRecord) {
            const vs = <SingleViewStateRecord>RecordFactory.createViewStateForType(state.viewState.viewStateType);
            state.viewState.copyDataTo(vs, true);

            const filter = state.viewState.viewStateFilter.deepCopy();
            if (action.required) {
                filter.requiredFilters = filter.requiredFilters.filter(f => f.ID !== action.filter);
                filter.onFilterStateChange(true);
            } else {
                filter.optionalFilters = filter.optionalFilters.filter(f => f.ID !== action.filter);
                filter.onFilterStateChange(false);
            }
            vs.viewStateFilter = filter;

            return {
                ...state,
                viewState: vs,
            };
        }
        return state;
    }),
    on(ViewStateActions.updateViewStateFilter, state => {
        if (state.viewState) {
            const vs = state.viewState as SingleViewStateRecord;
            const filter = vs.viewStateFilter.deepCopy();
            vs.viewStateFilter = filter;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(ViewStateActions.adjustDateRange, (state, action): ViewStateState => {
        if (state.viewState instanceof SingleViewStateRecord) {
            const vs = state.viewState as SingleViewStateRecord;
            const filter = vs.viewStateFilter.deepCopy();
            const targetDateFilter: FilterDef = filter.filters.filter(f => f.ID === PSTFieldNamesEnum.TargetPublicationDate)[0];
            if (targetDateFilter) {
                const dateRange = new DateRange();
                dateRange.from = action.firstDate;
                dateRange.to = action.lastDate;
                dateRange.custom = null;
                targetDateFilter.Value = dateRange;
                vs.viewStateFilter = filter;
                return handleUpdateViewState(state, vs);
            }
        } else if (state.viewState instanceof PublicationViewStateRecord) {
            // const vs = state.viewState as PublicationViewStateRecord;
            // const filter = vs.viewStateFilter.deepCopy();
            // let targetDateFilter: FilterDef = filter.filters.find(f => f.ID === PSTFieldNamesEnum.PSTPublicationTime);
            // if (!targetDateFilter) {
            //     targetDateFilter = new FilterDef();
            //     targetDateFilter.ID = PSTFieldNamesEnum.PSTPublicationTime;
            //     targetDateFilter.PSTField = PSTFieldNamesEnum.PSTPublicationTime;
            //     targetDateFilter.Validation = FieldDefValidations.PSTPUBLICATIONLISTTIME;
            //     filter.filters.push(targetDateFilter);
            // }
            // if (targetDateFilter) {
            //     const dateRange = new DateRange();
            //     dateRange.from = action.firstDate;
            //     dateRange.to = action.lastDate;
            //     dateRange.custom = null;
            //     targetDateFilter.Value = dateRange;
            //     vs.viewStateFilter = filter;
            //     return handleUpdateViewState(state, vs);
            // }

            const vs = state.viewState as PublicationViewStateRecord;
            let time: DateTime = vs.publicationTime;
            if (!time || !time.get()) {
                const dt = new DateTime();
                const d = new Date();
                d.setHours(0, 0, 0, 0);
                dt.set(d);
                (<PublicationViewStateRecord>vs).publicationTime = dt;
                time = dt;
            }

            if (action.firstDate) {
                time.set(action.firstDate);
            }
            vs.publicationTime = time;
            return handleUpdateViewState(state, vs);
        }

        return state;
    }),
    on(ViewStateActions.updateViewStateFilterState, (state, action) => {
        if (state.viewState) {
            const vs = state.viewState as ViewStateRecord;
            vs.viewStateFilterState = action.opened;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(ViewStateActions.updateViewStateLayout, (state, action) => {
        if (state.viewState) {
            const vs = state.viewState as SingleViewStateRecord;
            vs.viewStateLayout.Value = action.layout;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(ViewStateActions.updateViewStateGroupBy, (state, action) => {
        if (state.viewState) {
            const vs = state.viewState as SingleViewStateRecord;
            vs.viewStateGrouping.Value = action.groupBy;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(ViewStateActions.updateViewStateSortBy, (state, action) => {
        if (state.viewState) {
            const vs = state.viewState as SingleViewStateRecord;
            vs.viewStateSorting.Value = action.sortBy;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(ViewStateActions.toggleViewStateZoomToCoverage, state => {
        if (state.viewState && state.viewState instanceof TopicViewStateRecord) {
            const vs = state.viewState as TopicViewStateRecord;
            vs.viewStateMapZoomToCoverage = !vs.viewStateMapZoomToCoverage;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(ViewStateActions.toggleViewStateShowTeamLocations, state => {
        if (state.viewState && state.viewState instanceof TopicViewStateRecord) {
            const vs = state.viewState as TopicViewStateRecord;
            vs.viewStateMapShowTeamLocations = !vs.viewStateMapShowTeamLocations;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(ViewStateActions.toggleViewStateLockMapLocation, state => {
        if (state.viewState && state.viewState instanceof TopicViewStateRecord) {
            const vs = state.viewState as TopicViewStateRecord;
            vs.viewStateMapLockMapLocation = !vs.viewStateMapLockMapLocation;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(ViewStateActions.updateViewStateGroupBySortOrder, state => {
        if (state.viewState) {
            const vs = state.viewState as SingleViewStateRecord;
            vs.viewStateGroupSortOrder = vs.viewStateGroupSortOrder === ViewStateSortOders.ASC ? ViewStateSortOders.DESC : ViewStateSortOders.ASC;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(ViewStateActions.updateViewStateSortBySortOrder, state => {
        if (state.viewState) {
            const vs = state.viewState as SingleViewStateRecord;
            vs.viewStateSortOrder = vs.viewStateSortOrder === ViewStateSortOders.ASC ? ViewStateSortOders.DESC : ViewStateSortOders.ASC;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),

    on(ViewStateActions.updateViewStateRevisionNumber, (state, action) => {
        if (state.viewState && state.viewState instanceof PublicationViewStateRecord) {
            const vs = state.viewState as PublicationViewStateRecord;
            vs.publicationRevisionNumber = action.revisionNumber;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(ViewStateActions.updateViewStateMaxRevisionNumber, (state, action) => {
        if (state.viewState && state.viewState instanceof PublicationViewStateRecord) {
            const vs = state.viewState as PublicationViewStateRecord;
            vs.maxPublicationRevisionNumber = (action.vs as PublicationViewStateRecord).maxPublicationRevisionNumber;
            vs.publicationRevisionNumber = (action.vs as PublicationViewStateRecord).publicationRevisionNumber;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(ViewStateActions.updateViewStateViewStateGuid, (state, action) => {
        if (state.viewState && state.viewState instanceof PublicationViewStateRecord) {
            const vs = state.viewState as PublicationViewStateRecord;
            vs.viewStateGUID = action.guid;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(ViewStateActions.toggleFilterState, state => {
        if (state.viewState) {
            const vs = state.viewState as ViewStateRecord;
            vs.viewStateFilterState = !vs.viewStateFilterState;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(ViewStateActions.toggleFilterElevation, state => {
        const elevation: ViewStateFilterElevations = state.filterElevation === 'over' ? 'side' : 'over';
        if (state.viewState) {
            const vs = state.viewState as ViewStateRecord;
            return handleUpdateViewState({ ...state, filterElevation: elevation }, vs);
        } else {
            return { ...state, filterElevation: elevation };
        }
    }),
    on(ViewStateActions.togglePlanningViewsFilterState, (state): ViewStateState => {
        return { ...state, planningViewsFilterState: state.planningViewsFilterState === ViewStateFilterStates.Expanded ? ViewStateFilterStates.Collapsed : ViewStateFilterStates.Expanded };
    }),
    on(ViewStateActions.toggleSortOder, state => {
        if (state.viewState && state.viewState instanceof SingleViewStateRecord) {
            const vs = state.viewState as SingleViewStateRecord;
            vs.viewStateSortOrder = vs.viewStateSortOrder === ViewStateSortOders.ASC ? ViewStateSortOders.DESC : ViewStateSortOders.ASC;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(ViewStateActions.updateViewStateCurrentMapCenter, (state, action) => {
        if (state.viewState && state.viewState instanceof TopicViewStateRecord) {
            const vs = state.viewState as TopicViewStateRecord;
            vs.viewStateCurrentMapCenter = action.center;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(ViewStateActions.updateViewStateCurrentMapZoom, (state, action) => {
        if (state.viewState && state.viewState instanceof TopicViewStateRecord) {
            const vs = state.viewState as TopicViewStateRecord;
            vs.viewStateMapCurrentZoom = action.zoom;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(ViewStateActions.toggleGroupSortOder, state => {
        if (state.viewState && state.viewState instanceof SingleViewStateRecord) {
            const vs = state.viewState as SingleViewStateRecord;
            vs.viewStateGroupSortOrder = vs.viewStateGroupSortOrder === ViewStateSortOders.ASC ? ViewStateSortOders.DESC : ViewStateSortOders.ASC;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(ViewStateActions.updateViewStateSearchTitle, state => {
        if (state.viewState && state.viewState instanceof SearchViewStateRecord) {
            const vs = state.viewState as SearchViewStateRecord;
            vs.title = vs.viewStateSearchQuery;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(ViewStateApiActions.applyTeamCustomCardFieldsSuccess, ViewStateActions.updateViewStateCustomCardFields, (state, action) => {
        if (state.viewState) {
            if (state.viewState instanceof TopicViewStateRecord || state.viewState instanceof SearchViewStateRecord || state.viewState instanceof PublicationViewStateRecord) {
                const vs = state.viewState as SingleViewStateRecord;
                vs.viewStateCustomCardItems = Object.assign([], action.fields);
                vs.isViewStateCustomCardItemsBlank = state.viewState.viewStateCustomCardItems && state.viewState.viewStateCustomCardItems.length > 0 ? false : true;
                return handleUpdateViewState(state, vs);
            }
        }
        return state;
    }),
    on(ViewStateActions.updateTemplateRefs, (state, action): ViewStateState => {
        return { ...state, mainCta: action.mainCta, secondaryCta: action.secondaryCta, viewOptions: action.viewOptions, rightSlot: action.rightSlot };
    }),
    on(NavigationApiActions.updateNavigationItemSuccess, (state, action): ViewStateState => {
        // TODO: we could add the default here????
        if (action.vs instanceof PublicationViewStateRecord) {
            const vs = action.vs as PublicationViewStateRecord;
            // add publication time
            const publicationTimeFilter = vs.viewStateFilter.allFilters.find(f => f.ID === PSTFieldNamesEnum.PSTPublicationTime);
            if (publicationTimeFilter?.Value && PUBLICATION_LIST_TIME_DAYS_MAP[publicationTimeFilter.Value] != null) {
                const dt = new DateTime();
                const d = new Date();
                d.setHours(0, 0, 0, 0);
                d.setDate(d.getDate() + PUBLICATION_LIST_TIME_DAYS_MAP[publicationTimeFilter.Value]);
                dt.set(d);
                vs.publicationTime = dt;
            }
        }
        return handleUpdateViewState(state, action.vs);
    })
);
